import * as React from "react";
import { cn } from "@/lib/utils";

interface ScrollFadeWrapperProps {
  children: React.ReactNode;
  className?: string;
  fadeColor?: string;
}

/**
 * Wraps a horizontally-scrollable element (e.g. TabsList) and renders
 * gradient fade indicators on the edges that appear/disappear based on
 * scroll position.
 */
const ScrollFadeWrapper = ({ children, className, fadeColor = "from-muted" }: ScrollFadeWrapperProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const checkScroll = React.useCallback(() => {
    const el = containerRef.current?.querySelector("[data-scroll-target], [class*='overflow']") as HTMLElement
      ?? containerRef.current?.firstElementChild as HTMLElement;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  React.useEffect(() => {
    const container = containerRef.current;
    const el = container?.querySelector("[data-scroll-target], [class*='overflow']") as HTMLElement
      ?? container?.firstElementChild as HTMLElement;
    if (!el) return;

    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div
        className={cn(
          "pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-r to-transparent rounded-l-md transition-opacity duration-200",
          fadeColor,
          canScrollLeft ? "opacity-100" : "opacity-0"
        )}
        aria-hidden
      />
      <div
        className={cn(
          "pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-l to-transparent rounded-r-md transition-opacity duration-200",
          fadeColor,
          canScrollRight ? "opacity-100" : "opacity-0"
        )}
        aria-hidden
      />
      {children}
    </div>
  );
};

export { ScrollFadeWrapper };
